/**
 * jai1 tasks groups [-s status] [-j]
 * aliases: parents, pg
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { TaskService } from '../../services/task.service.js';
import type { TaskParentsOptions, ParentInfo } from '../../types/task.types.js';

const PARENT_STATUS_ICONS: Record<string, string> = {
    done: '✅',
    in_progress: '🔵',
    ready: '📋',
    todo: '🔴',
};

function formatProgress(p: ParentInfo): string {
    const completed = p.done + p.cancelled;
    if (p.status === 'done') {
        return chalk.green(`${completed}/${p.total} done`);
    }

    const parts: string[] = [];
    if (p.in_progress > 0) parts.push(`${p.in_progress} in_progress`);
    if (p.ready > 0) parts.push(`${p.ready} ready`);
    if (p.blocked > 0) parts.push(chalk.red(`${p.blocked} blocked`));
    if (p.done > 0) parts.push(`${p.done} done`);

    return `${completed}/${p.total} tasks` + (parts.length > 0 ? ` (${parts.join(', ')})` : '');
}

export function createTaskParentsCommand(): Command {
    return new Command('groups')
        .aliases(['parents', 'pg'])
        .description('List task groups with computed status')
        .option('-s, --status <status>', 'Filter by computed status: done, in_progress, ready, todo')
        .option('-j, --json', 'Output JSON')
        .action(async (options: TaskParentsOptions) => {
            const service = new TaskService();
            const parents = await service.getParents(options.status);

            if (options.json) {
                console.log(JSON.stringify(parents, null, 2));
                return;
            }

            if (parents.length === 0) {
                if (options.status) {
                    console.log(chalk.dim(`No groups with status: ${options.status}`));
                } else {
                    console.log(chalk.dim('No task groups found.'));
                    console.log(chalk.dim('💡 Add tasks with parent: jai1 t add "..." -P feature/xxx'));
                }
                return;
            }

            const header = options.status
                ? `📦 Task Groups — ${options.status} (${parents.length})`
                : `📦 Task Groups (${parents.length})`;
            console.log(chalk.bold(header));
            console.log();

            for (const p of parents) {
                const icon = PARENT_STATUS_ICONS[p.status] || '📋';
                const progress = formatProgress(p);
                console.log(`  ${icon}  ${chalk.bold(p.name)}  ${chalk.dim('—')}  ${progress}`);
            }

            console.log();
        });
}
